import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { coachingAPI } from '../services/api';

// How often the coach-view count is re-read while it's on.
export const COACH_COUNT_POLL_MS = 30000;

// The human-coaching unread count for a coach viewing an athlete, polled from
// /coaching/unread-count: the count is then the coach's own, not the
// athlete's the v2 state carries. V2Provider turns it on
// (meReader.opensCoachHub); everyone's own badge is v2's coaching_unread
// (useCoachingBadge, aithlo-frontend#67). The v1 /notifications/stream hub
// this used to open is gone (aithlo-backend#167).
//
// The last-seen count is cached so the badge isn't blank before the first
// answer, and cleared on logout so a different user doesn't inherit it.
const COUNTS_STORAGE_KEY = 'aithlo.notifCounts';

const loadCachedCount = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(COUNTS_STORAGE_KEY) || 'null');
    return typeof parsed?.coachingCount === 'number' ? parsed.coachingCount : 0;
  } catch {
    return 0;
  }
};

const NotificationSSEContext = createContext();

export const NotificationSSEProvider = ({ children }) => {
  const { user } = useAuth();
  const [coachingCount, setCoachingCount] = useState(loadCachedCount);
  const [hub, setHubState] = useState(false);
  const setHub = useCallback((on) => setHubState(Boolean(on)), []);
  const timerRef = useRef(null);
  // A stable key: the poll restarts on sign-in/out or a hub change, not on
  // every new `user` object.
  const signedIn = Boolean(user);

  useEffect(() => {
    try {
      localStorage.setItem(COUNTS_STORAGE_KEY, JSON.stringify({ coachingCount }));
    } catch {
      // localStorage may be disabled (private mode): the cache is an optimisation.
    }
  }, [coachingCount]);

  const refreshCounts = useCallback(async () => {
    if (!signedIn || !hub) return;
    try {
      const data = await coachingAPI.getUnreadCount();
      setCoachingCount(data.count || 0);
    } catch {
      // silent — user may not be logged in
    }
  }, [signedIn, hub]);

  useEffect(() => {
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    if (signedIn && hub) {
      refreshCounts();
      timerRef.current = setInterval(refreshCounts, COACH_COUNT_POLL_MS);
    } else if (!signedIn) {
      // Logout: zero the count and wipe the cache.
      setCoachingCount(0);
      try { localStorage.removeItem(COUNTS_STORAGE_KEY); } catch { /* ignore */ }
    }
    return stop;
  }, [signedIn, hub, refreshCounts]);

  return (
    <NotificationSSEContext.Provider value={{ coachingCount, refreshCounts, hub, setHub }}>
      {children}
    </NotificationSSEContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationSSEContext);
